
import { GlassCard } from "@/components/ui/glass-card";
import { Button } from "@/components/ui/button";
import { MapPin, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { type InstalledApp } from "@/hooks/useDevices";
import DeviceCardHeader from "./device/DeviceCardHeader";
import DeviceCardInfo from "./device/DeviceCardInfo";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Device {
  id: string;
  name: string;
  os: string;
  status: string;
  lastSeen: string;
  ip: string;
  installed_apps: InstalledApp[];
  battery_level?: number | null;
  battery_status?: string | null;
  serial_number?: string | null;
  department?: string;
  location?: string;
}

const DeviceCard = ({ device }: { device: Device }) => {
  const navigate = useNavigate();

  const triggerHeartbeat = async () => {
    try {
      console.log('Triggering heartbeat for device:', device.id);
      
      const { data: enrollmentToken, error } = await supabase.rpc(
        'get_device_enrollment_token', 
        { device_id: device.id }
      );

      if (error || !enrollmentToken) {
        console.error('No enrollment token found for device:', device.id, error);
        toast.error('Device enrollment token not found or access denied');
        return;
      }

      console.log('Invoking device-heartbeat function...');
      const { data, error: invokeError } = await supabase.functions.invoke('device-heartbeat', {
        body: {
          device_id: device.id,
          enrollment_token: enrollmentToken
        }
      });

      if (invokeError) {
        console.error('Failed to trigger heartbeat:', invokeError);
        toast.error('Failed to trigger device heartbeat');
        return;
      }

      console.log('Heartbeat response:', data);
      toast.success('Device check-in successful');
    } catch (error) {
      console.error('Error in triggerHeartbeat:', error);
      toast.error('Failed to trigger device heartbeat');
    }
  };

  const handleUnenroll = async () => {
    try {
      const { error } = await supabase
        .from('devices')
        .update({
          status: 'unenrolled',
          last_seen: new Date().toISOString()
        })
        .eq('id', device.id);

      if (error) throw error;

      toast.success('Device unenrolled successfully');
    } catch (error) {
      console.error('Error unenrolling device:', error);
      toast.error('Failed to unenroll device');
    }
  };

  return (
    <GlassCard variant="default" className="p-6">
      <DeviceCardHeader
        name={device.name}
        status={device.status}
        onCheckIn={triggerHeartbeat}
        onUnenroll={handleUnenroll}
      />
      <DeviceCardInfo
        os={device.os}
        osType={device.os.split(' ')[0]}
        deviceName={device.name}
        lastSeen={device.lastSeen}
        ip={device.ip}
        serialNumber={device.serial_number}
        batteryLevel={device.battery_level}
        batteryStatus={device.battery_status}
        installedApps={device.installed_apps}
      />
      
      {(device.department || device.location) && (
        <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
          {device.department && (
            <div className="flex items-center text-sm text-gray-500">
              <Users className="h-4 w-4 mr-2 text-gray-400" />
              {device.department}
            </div>
          )}
          {device.location && (
            <div className="flex items-center text-sm text-gray-500">
              <MapPin className="h-4 w-4 mr-2 text-gray-400" />
              {device.location}
            </div>
          )}
        </div>
      )}
      
      <div className="mt-4">
        <Button 
          variant="outline" 
          className="w-full"
          onClick={() => navigate(`/devices/${device.id}`)}
        >
          View Details
        </Button>
      </div>
    </GlassCard>
  );
};

export default DeviceCard;
